import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const KEY = "perfero_cart_v1";
const CartContext = createContext(null);

const readCart = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((i) => i?.slug && i?.quantity > 0) : [];
  } catch {
    return [];
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(readCart);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.slug === product.slug);
      if (found) {
        return prev.map((i) => (i.slug === product.slug ? { ...i, quantity: Math.min(20, i.quantity + quantity) } : i));
      }
      const { slug, name, price, image, size } = product;
      return [...prev, { slug, name, price, image, size, quantity }];
    });
    setOpen(true);
  }, []);

  const update = useCallback((slug, quantity) => {
    setItems((prev) =>
      quantity <= 0 ? prev.filter((i) => i.slug !== slug) : prev.map((i) => (i.slug === slug ? { ...i, quantity: Math.min(20, quantity) } : i)),
    );
  }, []);

  const remove = useCallback((slug) => setItems((prev) => prev.filter((i) => i.slug !== slug)), []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const count = items.reduce((sum, i) => sum + i.quantity, 0);
    const subtotal = Math.round(items.reduce((sum, i) => sum + i.price * i.quantity, 0) * 100) / 100;
    return { items, count, subtotal, open, setOpen, add, update, remove, clear };
  }, [items, open, add, update, remove, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
